import React, { useState } from 'react';
import {
  AlertTriangle, ArrowRight, Calendar, CheckCircle, Clock, FileText,
  Download, Mail, User, Plus, Wrench, XCircle, ChevronDown, ChevronUp,
  Shield, Fuel, MapPin, FileCheck
} from 'lucide-react';
import { getDocumentUrl } from '../../api.js';
import { useFormat } from '../../i18n/format.js';
import { useTranslation } from 'react-i18next';

/**
 * DossierTimeline - Specialized timeline component for workflow dossiers
 *
 * Features:
 * - Chronological event display with proper workflow icons
 * - Document links with download capability
 * - Email sent indicators
 * - Actor/user information
 * - Notes and rejection reason display
 * - Expandable details
 */

// Icon/color mapping for different event/action types.
// Labels are NOT stored here (i18n piege #1: a module constant is evaluated once,
// before any language switch) — they are resolved at render via t().
const TYPE_CONFIG = {
  incident: { icon: AlertTriangle, color: 'bg-red-500' },
  workflow: { icon: ArrowRight, color: 'bg-blue-500' },
  event: { icon: Calendar, color: 'bg-gray-500' },
  created: { icon: Plus, color: 'bg-green-500' },
  document: { icon: FileText, color: 'bg-purple-500' },
  email: { icon: Mail, color: 'bg-indigo-500' },
  approval: { icon: CheckCircle, color: 'bg-emerald-500' },
  rejection: { icon: XCircle, color: 'bg-red-600' },
  maintenance: { icon: Wrench, color: 'bg-orange-500' },
  insurance: { icon: Shield, color: 'bg-blue-600' },
  fuel: { icon: Fuel, color: 'bg-amber-500' },
  assignment: { icon: User, color: 'bg-indigo-500' },
  inspection: { icon: FileCheck, color: 'bg-teal-500' },
  location: { icon: MapPin, color: 'bg-cyan-500' },
  default: { icon: Clock, color: 'bg-gray-400' }
};

function TimelineEntry({ entry, isLast, expanded, onToggle }) {
  const { t, fmt } = useFormat();
  // Determine type config
  let typeKey = entry.type || 'default';

  // Special handling for workflow transitions
  if (typeKey === 'workflow' && entry.to_status) {
    if (entry.to_status === 'rejected' || entry.to_status === 'cancelled') {
      typeKey = 'rejection';
    } else if (entry.to_status === 'approved' || entry.to_status === 'completed') {
      typeKey = 'approval';
    }
  }

  const config = TYPE_CONFIG[typeKey] || TYPE_CONFIG.default;
  const IconComponent = config.icon;
  const configLabel = t(`common.timeline.types.${typeKey}`, {
    defaultValue: t('common.timeline.types.default')
  });

  // Overdue detection: red pulse when deadline_date is past and step not completed
  const isOverdue = entry.deadline_date && !entry.completed &&
    new Date(entry.deadline_date) < new Date();

  const formatDate = (date) => {
    if (!date) return '';
    return fmt.date(date, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    return t(`common.timeline.statuses.${status}`, { defaultValue: status });
  };

  const hasDetails = entry.description || entry.action_notes || entry.rejection_reason ||
                     entry.documents?.length > 0 || entry.emails?.length > 0;

  return (
    <div className="relative flex gap-4">
      {/* Timeline line */}
      {!isLast && (
        <div className="absolute left-5 top-10 bottom-0 w-0.5 bg-gray-200" />
      )}

      {/* Icon */}
      <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full ${isOverdue ? 'bg-red-500 animate-pulse ring-2 ring-red-300' : config.color} flex items-center justify-center shadow-md`}>
        <IconComponent className="w-5 h-5 text-white" />
      </div>

      {/* Content */}
      <div className="flex-1 pb-6">
        <div
          className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${
            hasDetails ? 'cursor-pointer hover:shadow-md transition-shadow' : ''
          }`}
          onClick={() => hasDetails && onToggle && onToggle()}
        >
          {/* Header */}
          <div className="px-4 py-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                {/* Type badge and status transition */}
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${config.color} text-white`}>
                    {configLabel}
                  </span>

                  {/* Overdue badge */}
                  {isOverdue && (
                    <span className="text-xs font-bold px-1.5 py-0.5 bg-red-100 text-red-700 rounded animate-pulse">
                      {t('common.timeline.overdueBadge')}
                    </span>
                  )}

                  {/* Show workflow transition */}
                  {entry.from_status && entry.to_status && (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                      <span className="px-1.5 py-0.5 bg-gray-100 rounded">
                        {getStatusLabel(entry.from_status)}
                      </span>
                      <ArrowRight className="w-3 h-3" />
                      <span className={`px-1.5 py-0.5 rounded ${
                        entry.to_status === 'rejected' ? 'bg-red-100 text-red-700' :
                        entry.to_status === 'approved' || entry.to_status === 'completed' ? 'bg-green-100 text-green-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {getStatusLabel(entry.to_status)}
                      </span>
                    </span>
                  )}
                </div>

                {/* Title */}
                <h4 className="font-semibold text-gray-900 truncate">{entry.title}</h4>

                {/* Actor info */}
                {(entry.actor || entry.triggered_by_name) && (
                  <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                    <User className="w-3 h-3" />
                    <span>{t('common.timeline.byActor', { name: entry.actor || entry.triggered_by_name })}</span>
                  </div>
                )}
              </div>

              {/* Date and indicators */}
              <div className="text-right flex-shrink-0">
                <p className="text-sm text-gray-500">{formatDate(entry.date || entry.created_at)}</p>

                {/* Output indicators */}
                <div className="flex items-center justify-end gap-1 mt-1">
                  {entry.email_sent && (
                    <span className="p-1 bg-indigo-100 rounded" title={t('common.timeline.emailSentTitle')}>
                      <Mail className="w-3 h-3 text-indigo-600" />
                    </span>
                  )}
                  {entry.document_generated && (
                    <span className="p-1 bg-purple-100 rounded" title={t('common.timeline.documentGeneratedTitle')}>
                      <FileText className="w-3 h-3 text-purple-600" />
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Expandable details */}
          {hasDetails && expanded && (
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 space-y-3">
              {/* Description or notes */}
              {(entry.description || entry.action_notes) && (
                <p className="text-sm text-gray-700">
                  {entry.description || entry.action_notes}
                </p>
              )}

              {/* Rejection reason */}
              {entry.rejection_reason && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-800">{t('common.timeline.rejectionReasonLabel')}</p>
                  <p className="text-sm text-red-700 mt-1">{entry.rejection_reason}</p>
                </div>
              )}

              {/* Documents */}
              {entry.documents && entry.documents.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase">{t('common.timeline.documentsLabel')}</p>
                  <div className="flex flex-wrap gap-2">
                    {entry.documents.map((doc, idx) => (
                      <a
                        key={idx}
                        href={getDocumentUrl(doc) || doc.url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm hover:bg-gray-50 transition"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FileText className="w-4 h-4 text-purple-500" />
                        <span className="text-gray-700">
                          {doc.doc_name || doc.name || t('common.timeline.documentFallbackName', { index: idx + 1 })}
                        </span>
                        <Download className="w-3 h-3 text-gray-400" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Emails sent */}
              {entry.emails && entry.emails.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase">{t('common.timeline.emailsSentLabel')}</p>
                  <div className="space-y-1">
                    {entry.emails.map((email, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm"
                      >
                        <Mail className="w-4 h-4 text-indigo-500" />
                        <span className="text-gray-700">{email.recipient || email.to}</span>
                        <span className="text-gray-400">-</span>
                        <span className="text-gray-600 truncate">{email.subject}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional metadata */}
              {entry.details && typeof entry.details === 'object' && (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(entry.details).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-500">{key}</span>
                      <span className="text-gray-900 font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Expand indicator */}
          {hasDetails && (
            <div className="px-4 py-1.5 bg-gray-50 border-t border-gray-100 flex items-center justify-center">
              {expanded ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DossierTimeline({
  timeline = [],
  workflowActions = [],
  documents = [],
  dossierType = 'claim',
  sortOrder = 'desc', // 'asc' or 'desc'
  maxItems = null,
  showLoadMore = true,
  emptyMessage = null,
  className = ''
}) {
  const { t } = useTranslation();
  const [expandedEntries, setExpandedEntries] = useState({});
  const [displayCount, setDisplayCount] = useState(maxItems || 20);

  const toggleEntry = (index) => {
    setExpandedEntries(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Combine timeline entries with workflow actions if both are provided
  const allEntries = React.useMemo(() => {
    const combined = [...timeline];

    // Add workflow actions if not already in timeline
    if (workflowActions.length > 0) {
      workflowActions.forEach(action => {
        // Check if this action is already in timeline by matching id or timestamp
        const exists = combined.some(entry =>
          entry.id === action.id ||
          (entry.type === 'workflow' && entry.date === action.created_at)
        );

        if (!exists) {
          combined.push({
            id: action.id,
            date: action.created_at,
            type: 'workflow',
            title: action.step_name || t('common.timeline.types.workflow'),
            from_status: action.from_status,
            to_status: action.to_status,
            actor: action.triggered_by_name,
            action_notes: action.action_notes,
            rejection_reason: action.rejection_reason,
            email_sent: action.email_sent,
            document_generated: action.document_generated,
            email_id: action.email_id,
            document_id: action.document_id
          });
        }
      });
    }

    // Sort by date
    combined.sort((a, b) => {
      const dateA = new Date(a.date || a.created_at);
      const dateB = new Date(b.date || b.created_at);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    return combined;
  }, [timeline, workflowActions, sortOrder, t]);

  const displayedEntries = allEntries.slice(0, displayCount);
  const hasMore = displayCount < allEntries.length;

  if (allEntries.length === 0) {
    return (
      <div className={`text-center py-12 text-gray-500 ${className}`}>
        <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p>{emptyMessage || t('common.timeline.emptyMessage')}</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Timeline header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Clock className="w-5 h-5 text-gray-500" />
          {t('common.timeline.historyTitle', { count: allEntries.length })}
        </h3>
        {documents.length > 0 && (
          <span className="text-sm text-gray-500 flex items-center gap-1">
            <FileText className="w-4 h-4" />
            {t('common.timeline.documentsCount', { count: documents.length })}
          </span>
        )}
      </div>

      {/* Timeline entries */}
      <div>
        {displayedEntries.map((entry, index) => (
          <TimelineEntry
            key={entry.id || index}
            entry={entry}
            isLast={index === displayedEntries.length - 1}
            expanded={expandedEntries[index]}
            onToggle={() => toggleEntry(index)}
          />
        ))}
      </div>

      {/* Load more button */}
      {showLoadMore && hasMore && (
        <div className="text-center pt-4">
          <button
            onClick={() => setDisplayCount(prev => prev + 10)}
            className="px-6 py-2 text-sm font-medium text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
          >
            {t('common.timeline.loadMore', { count: allEntries.length - displayCount })}
          </button>
        </div>
      )}
    </div>
  );
}

export default DossierTimeline;
